/* =============================================================================
   ics-layout — the page-layout module of the library: the ONE z-index scale,
   the five-region border layout, and the application shell that runs on it.

   Three sections, one module:

     LayoutService  — the library's ONE z-index scale + geometry helpers.
                      ics-window.js and ics-messager.js import it from HERE.
     Layout         — the own five-region border layout (north / south / east /
                      west / center, collapsible edges, peek bars, drag
                      splitters). Authored as `<div data-ics="layout">`; it HOSTS
                      THE APP SHELL on Views\Shared\_Layout.cshtml. The jQuery
                      plugin name is `$(el).layout(...)` — the ENTRY installs the
                      dispatcher, and a receiver this component does not own is
                      passed to the handler installed before it, untouched.
     Shell          — the app shell behaviour: the frame shell (area 2 = the
                      screen <iframe>, URL/title/menu sync), the menu tree, the
                      welcome notice, the white-label flag, the sidebar
                      auto-collapse (bindSidebarCollapse, the 1100px breakpoint,
                      sessionStorage 'menucollapse'), the three user-panel
                      popovers + the RECENT ALERTS list (bindShellPopovers), and
                      the PAGE GLOBALS every view calls (icsNavigateTo,
                      icsCloseWindow, the notification functions, readURL,
                      performLogout, the backspace guard). The .cshtml carries
                      MARKUP + one JSON config (`window.IcsShellConfig`: the
                      Razor URLs and the session values) and nothing else.

   The sheet is lib-v2/css/components/ics-layout.css (region chrome + the shell
   chrome + the framed-screen base rules). The bundle is
   dist-v2/ics-layout.min.js from index.layout.js, which REGISTERS the
   component: Registry.add lives in the ENTRY, not here. This module is
   imported by other bundles for LayoutService and must have no side effect —
   importing a component into another entry re-registers it there.

   ⚠ THE REGION PANELS ARE THE CONTAINER BUNDLE'S CLASS. Registry / Component
   classes are per bundle; a static `import { Container }` here would register
   Panel/Tabs/Accordion inside every bundle that imports this module (the
   dialogs bundle loads BEFORE the container bundle and its ready pass would
   mount every panel on the page with the wrong class). So the Layout takes the
   Container class from the realm handle `Ics.components.Container` at mount
   time — the container bundle must load earlier on the page — and a region
   therefore answers `.panel('options')` through the container bundle's own
   dispatcher.
   ============================================================================= */
import { Component } from '../core/component.js';
import { parseDataOptions } from '../core/options.js';
import { cssVarNum, placePanel, pageRect } from '../core/dom.js';
import { Registry } from '../services/registry.js';

const jq = () => globalThis.jQuery || null;

/* =============================================================================
   LayoutService: geometry helpers plus the ONE z-index scale of the
   library. The bands are design tokens, so no component may invent a z value of
   its own: the window/picker counter starts at --ics-z-window, climbs
   monotonically (two at a time, for a window and its mask) and is CAPPED below
   --ics-z-dropdown, so a dropdown always paints above any stacked window. The
   counter is never reset while the page lives — a re-opened window gets a NEW
   z, not its old one.
   API: z(band) · nextWindowZ() · place(panel, anchor, opts) · rect(el) ·
   clampPickerSize(w, h) · center(el, {width, height}).
   ============================================================================= */
let _windowZ = 0;   // stack counter state; seeded lazily from the token

export const LayoutService = {
  /** z band accessors (numbers, resolved from tokens once asked) */
  z(band) { return cssVarNum('--ics-z-' + band); },

  /** next z for a stacked window/picker: monotonic, never reset, and capped under
      the dropdown band so a dropdown always wins over any stacked window */
  nextWindowZ() {
    if (!_windowZ) _windowZ = this.z('window') || 1000;
    _windowZ += 2;                                   // window + its mask/shadow pair
    const ceiling = (this.z('dropdown') || 5000) - 10;
    if (_windowZ > ceiling) _windowZ = ceiling;      // never invade the dropdown band
    return _windowZ;
  },

  /** shared dropdown positioner (anchor-below w/ viewport flip; body-append) */
  place(panel, anchor, opts) { return placePanel(panel, anchor, opts); },

  rect(el) { return pageRect(el); },

  /** picker window size clamps: a width <= 180 becomes 400, a height <= 120 becomes 120 */
  clampPickerSize(w, h) {
    return { width: (!w || w <= 180) ? 400 : w, height: (!h || h <= 120) ? 120 : h };
  },

  /** scroll-aware centering for windows and dialogs; never places above/left of 4px */
  center(el, { width, height } = {}) {
    const w = width || el.offsetWidth, h = height || el.offsetHeight;
    el.style.left = Math.max(4, window.scrollX + (document.documentElement.clientWidth - w) / 2) + 'px';
    el.style.top = Math.max(4, window.scrollY + (document.documentElement.clientHeight - h) / 2) + 'px';
  },
};

/* =============================================================================
   Layout: the border layout that hosts a whole page: one container
   split into north / south / east / west / center regions, positioned
   absolutely by the component itself (setSize), with collapsible edges,
   peek-on-click bars and drag-resizable splitters.

   Each region is a REAL panel-role Container instance — its own header, border
   and LIVE options bag that callers may read. The generated class names
   layout, layout-panel-*, layout-expand*, layout-split-* and layout-button-*
   are PUBLIC API and are emitted verbatim: shell media queries target them and
   several views use the arrow classes standalone.

   AUTHORING: `<div data-ics="layout" data-options="fit:true">` whose regions
   are DIRECT child divs carrying region="north|south|east|west|center" (a bare
   attribute or data-options). Divs of a direct child <form> join the scan. Any
   other child is left completely alone, and when two children claim one region
   the FIRST wins.

   Options: fit on the container (a body layout is always fit, sized to the
   viewport). Per-region options are the region panel's own: title, iconCls,
   border, split, collapsible, collapsed, collapsedSize (28), minWidth /
   minHeight (10), maxWidth / maxHeight (10000), width / height.

   Methods: setSize, collapse(region[, animate]), expand(region[, animate]),
   isCollapsed(region), resize([{width,height}]), doLayout, panel(region),
   destroy. Adding or removing a region at runtime is not implemented.
   Static: Layout.autoResize — set false to stop div layouts following the
   window (body layouts keep tracking it).

   Rules and traps:
   - A method call on an EMPTY selector is a silent no-op, forever; so is a call
     naming a region this layout does not have, and layout('panel') for a
     missing region answers an EMPTY jQuery set rather than throwing.
   - Layouts relayout on window resize, debounced 100ms; a NESTED layout skips
     the hook because the top-level pass cascades down into it.
   - A region's size is captured ONCE from its inline style (the px of that
     moment). A size given as a STRING through data-options ('13%') stays
     live-proportional on every pass — until a splitter drop overwrites it
     with px.
   - Adjacent bordered non-split regions overlap by 1px, by design.
   - Collapsing swaps the region for a 28px expand bar: its arrow icon expands
     permanently, while clicking the bar BODY peeks — the panel slides OVER the
     content and re-collapses on mouseleave. A peek refuses to end while a
     dropdown is open or a splitter drag is running, and re-checks at the next
     mouseleave.
   - expand() is a NO-OP until the expand bar exists, i.e. after a collapse.
   - Rapid collapse/expand/collapse sequences are safe: the state flags flip
     SYNCHRONOUSLY and every animation callback re-checks them, so the final
     state is whatever the LAST call asked for.
   - collapsed:true in the markup applies with ZERO animation, and doLayout()
     re-applies those MARKUP collapsed states, not the current ones.
   - title:null = no header at all; title:'' = a header with empty text;
     collapsible:false hides the directional arrow but keeps the header.
   - A child that is ITSELF a layout gets a synthetic wrapper panelled around
     it, because storage is one component instance per element.
   - ⚠ Mounting MOVES the region content into panel wrappers. An <iframe> inside
     a region RELOADS when moved — the shell (below) assigns the screen frame's
     src only AFTER the mount, never in the markup.
   ============================================================================= */
/** THE SPLIT SEAM, in px. It is the region's split-edge border width in ics-layout.css (.layout-split-*)
    AND the drag target's thickness here; the two must agree, so the sheet states 3px where this
    constant states 3. */
const SPLIT = 3;
const ARROW = { north: 'up', south: 'down', east: 'right', west: 'left' };      // header collapse arrow
const BAR_ARROW = { north: 'down', south: 'up', east: 'left', west: 'right' };  // expand-bar arrow

/** the container bundle's Container class (realm handle) — the ONLY panel class a region may be */
function panelClass() {
  const Ics = globalThis.Ics;
  return (Ics && Ics.components && Ics.components.Container) || null;
}

export class Layout extends Component {
  static defaults = { fit: false };

  /** global switch: set false to stop div layouts following a window resize
      (they freeze; only body layouts keep tracking the window). */
  static autoResize = true;

  static REGIONS = ['north', 'south', 'east', 'west', 'center'];

  /** per-region defaults; collapsedSize is fixed at 28 */
  static PANEL_DEFAULTS = {
    split: false, border: true, collapsible: true, collapsed: false,
    collapsedSize: 28, minWidth: 10, minHeight: 10, maxWidth: 10000, maxHeight: 10000,
  };

  /** '13%' -> live px against the total; numbers pass through unchanged */
  static resolveSize(v, total) {
    if (typeof v === 'string' && v.endsWith('%')) return Math.floor(total * parseFloat(v) / 100);
    return typeof v === 'number' ? v : 0;
  }

  static resolveOptions(el, options) {
    const data = {};
    if (el && el.getAttribute) {
      const f = el.getAttribute('fit');                    // bare attribute variant
      if (f != null) data.fit = f === 'true';
    }
    if (el && el.dataset && el.dataset.options) Object.assign(data, parseDataOptions(el.dataset.options));
    return Object.assign({}, this.defaults, data, options || {});
  }

  /** a region's option bag parsed the way the container parses a panel's:
      bare attributes (region / title / iconCls / tools / fit / border / …)
      + data-options — through the container class when it is on the page,
      a data-options-only reading otherwise (Node harness) */
  static regionOptions(child) {
    const C = panelClass();
    if (C && typeof C.resolveOptions === 'function') return C.resolveOptions(child, { role: 'panel' });
    const o = { role: 'panel' };
    const r = child.getAttribute && child.getAttribute('region');
    if (r) o.region = r;
    if (child.dataset && child.dataset.options) Object.assign(o, parseDataOptions(child.dataset.options));
    return o;
  }

  _init() {
    const el = this.el, o = this.options;
    const C = panelClass();
    if (!C) throw new Error('[ics] ics-layout needs the container bundle (dist-v2/ics-container.min.js) loaded before it - Ics.components.Container is missing');
    this._Container = C;
    this._isBody = el.tagName === 'BODY';
    if (this._isBody) o.fit = true;                        // a body layout always fits
    el.classList.add('layout', 'ics-layout');
    this._regions = {};
    this._dragging = false;
    this._geom = { W: 0, H: 0, top: 0, bottom: 0 };

    // direct child divs; divs of a direct child <form> join the scan
    let kids = [...el.children].filter((n) => n.tagName === 'DIV');
    const form = [...el.children].find((n) => n.tagName === 'FORM');
    if (form) kids = kids.concat([...form.children].filter((n) => n.tagName === 'DIV'));

    const consumed = new Set();
    for (const child of kids) {
      const pOpts = Layout.regionOptions(child);            // ONE parser: bare attrs + data-options
      const dir = pOpts.region;
      if (!Layout.REGIONS.includes(dir)) continue;         // whitelist skip - non-region divs untouched
      if (this._regions[dir]) continue;                    // first region wins
      this._regions[dir] = this._makeRegion(child, pOpts, dir);
      consumed.add(child);
    }

    // A NON-REGION own-form container child is ADOPTED (mounted) here. The
    // container bundle's auto-mount guard skips EVERY direct child of a layout,
    // because from CSS it cannot tell a REGION child (which _makeRegion panels —
    // a second mount would double-frame it) from page content that merely sits
    // inside the layout box. The layout can tell: the regions are consumed
    // above, so an own-form child left over declares none and is the container
    // bundle's to mount — through the realm class, options parsed by that class
    // (data-ics carries the role). The estate shape this serves: a titled
    // grid-host panel inside a region-less vocabulary layout.
    for (const child of kids) {
      if (consumed.has(child)) continue;
      if (!child.matches || !child.matches('[data-ics="panel"],[data-ics="tabs"],[data-ics="accordion"]')) continue;
      if (!Component.get(child)) new C(child, {});
    }

    // the two reusable drag proxies - exactly one -h and one -v per layout
    this._proxyH = this._mkdiv('layout-split-proxy-h');
    this._proxyV = this._mkdiv('layout-split-proxy-v');

    this.setSize();
    this._applyInitialCollapsed();

    // always relayout on window resize (debounced 100ms); nested layouts
    // skip - the top-level pass cascades down into them
    this._rzTimer = null;
    if (globalThis.addEventListener) {
      this._events.add(globalThis, 'resize', () => {
        if (!Layout.autoResize && !this._isBody) return;   // switched off: div layouts freeze
        if (this._nestedInLayout()) return;
        if (this._rzTimer) this._events.clearTimer(this._rzTimer);
        this._rzTimer = this._events.timeout(() => this.doLayout(), 100);
      });
    }
  }

  _mkdiv(cls) {
    const d = document.createElement('div');
    d.className = cls;
    d.style.display = 'none';
    this.el.appendChild(d);
    return d;
  }

  _nestedInLayout() {
    const p = this.el.parentElement;
    return !!(p && p.closest && p.closest('.ics-layout'));
  }

  _makeRegion(child, pOpts, dir) {
    const Container = this._Container;
    const opts = Object.assign({}, Layout.PANEL_DEFAULTS, pOpts, { region: dir, fit: false });
    // capture the init-time px BEFORE Panel hoists the inline style;
    // a size STRING from data-options stays live-proportional
    const styleW = parseInt(child.style.width, 10);
    const styleH = parseInt(child.style.height, 10);
    if (dir === 'east' || dir === 'west') {
      if (typeof opts.width !== 'string' && typeof opts.width !== 'number') {
        opts.width = styleW || child.offsetWidth || 100;
      }
    } else if (dir === 'north' || dir === 'south') {
      if (typeof opts.height !== 'string' && typeof opts.height !== 'number') {
        opts.height = styleH || child.offsetHeight || 100;
      }
    }

    // dual-role: the child is itself a layout - panel a synthetic wrapper
    // (one component instance per element; the inner layout keeps the child)
    let target = child;
    if (child.classList.contains('ics-layout') || (child.matches && child.matches('[data-ics="layout"]')) || Component.get(child)) {
      const shell = document.createElement('div');
      child.parentNode.insertBefore(shell, child);
      shell.appendChild(child);
      target = shell;
    }

    const hasHeader = opts.title != null;                  // '' still builds a header, null = headerless
    const markupCollapsed = opts.collapsed === true;       // applied by the layout mechanics, not by Panel
    const inst = new Container(target, Object.assign({}, opts, {
      role: 'panel',
      noheader: !hasHeader, collapsed: false, closed: false,
      collapsible: false,                                  // suppress the panel's own arrow - replaced below
      toolbar: false,                                      // a region never auto-hoists a toolbar
    }));
    inst.options.collapsible = opts.collapsible;           // bag stays truthful for readers
    inst.wrap.classList.add('layout-panel', 'layout-panel-' + dir);
    target.classList.add('layout-body');                   // region body marker

    const rec = {
      dir, inst, opts: inst.options, wrap: inst.wrap, body: target,
      bar: null, splitter: null, collapsed: false, peeking: false,
      markupCollapsed, _dragSize: null,
    };

    if (hasHeader) {
      inst._ensureHeader();
      if (opts.collapsible !== false) {                    // the directional arrow tool
        const a = document.createElement('a');
        a.href = 'javascript:void(0)';
        a.className = 'layout-button-' + ARROW[dir];
        this._events.add(a, 'click', (ev) => {
          ev.stopPropagation();
          if (rec.collapsed) this.expand(dir); else this.collapse(dir);
        });
        inst._tool.appendChild(a);
      }
    }

    if (opts.split === true && dir !== 'center') {         // a split edge gets a splitter
      inst.wrap.classList.add('layout-split-' + dir);
      rec.splitter = this._makeSplitter(rec);
    }
    return rec;
  }

  /* ---- geometry -------------------------------------------------------------- */

  setSize() {
    const el = this.el, o = this.options;
    if (o.fit === true && !this._isBody && el.parentElement) {
      const p = el.parentElement, cs = getComputedStyle(p);
      const W = p.clientWidth - parseFloat(cs.paddingLeft || 0) - parseFloat(cs.paddingRight || 0);
      const H = p.clientHeight - parseFloat(cs.paddingTop || 0) - parseFloat(cs.paddingBottom || 0);
      el.style.width = Math.max(0, W) + 'px';
      el.style.height = Math.max(0, H) + 'px';
    } else if (this._isBody) {
      el.style.height = document.documentElement.clientHeight + 'px';   // viewport height
    }
    const W = el.clientWidth, H = el.clientHeight;
    let x1 = 0, y1 = 0, x2 = W, y2 = H;
    const R = this._regions;

    const place = (rec, left, top, w, h) => {
      rec.wrap.style.display = '';
      rec.wrap.style.left = left + 'px';
      rec.wrap.style.top = top + 'px';
      rec.wrap.style.width = Math.max(0, w) + 'px';
      rec.wrap.style.height = Math.max(0, h) + 'px';
      const headH = rec.inst._head ? rec.inst._head.offsetHeight : 0;
      const bdV = rec.wrap.offsetHeight - rec.wrap.clientHeight;         // actual borders (split edge = SPLIT px)
      rec.body.style.height = Math.max(0, h - headH - bdV) + 'px';
    };
    const placeBar = (rec, left, top, w, h) => {
      rec.bar.style.display = '';
      rec.bar.style.left = left + 'px';
      rec.bar.style.top = top + 'px';
      rec.bar.style.width = Math.max(0, w) + 'px';
      rec.bar.style.height = Math.max(0, h) + 'px';
    };
    const splitAt = (rec, left, top, w, h) => {
      rec.splitter.style.display = '';
      rec.splitter.style.left = left + 'px';
      rec.splitter.style.top = top + 'px';
      rec.splitter.style.width = w + 'px';
      rec.splitter.style.height = h + 'px';
    };

    if (R.north) {
      const r = R.north;
      if (r.collapsed && !r.peeking) { placeBar(r, 0, 0, W, r.opts.collapsedSize); y1 += r.opts.collapsedSize; }
      else if (!r.collapsed) {
        const h = Layout.resolveSize(r.opts.height, H);
        place(r, 0, y1, W, h);
        y1 += h;
        if (r.splitter) splitAt(r, 0, y1 - SPLIT, W, SPLIT);
        if (!r.opts.split && r.opts.border !== false) y1 -= 1;           // 1px border overlap
      }
    }
    if (R.south) {
      const r = R.south;
      if (r.collapsed && !r.peeking) { placeBar(r, 0, H - r.opts.collapsedSize, W, r.opts.collapsedSize); y2 -= r.opts.collapsedSize; }
      else if (!r.collapsed) {
        const h = Layout.resolveSize(r.opts.height, H);
        place(r, 0, y2 - h, W, h);
        y2 -= h;
        if (r.splitter) splitAt(r, 0, y2, W, SPLIT);
        if (!r.opts.split && r.opts.border !== false) y2 += 1;
      }
    }
    this._geom = { W, H, top: y1, bottom: y2 };                          // e/w band (peek geometry)
    if (R.east) {
      const r = R.east;
      if (r.collapsed && !r.peeking) { placeBar(r, W - r.opts.collapsedSize, y1, r.opts.collapsedSize, y2 - y1); x2 -= r.opts.collapsedSize; }
      else if (!r.collapsed) {
        const w = Layout.resolveSize(r.opts.width, W);
        place(r, x2 - w, y1, w, y2 - y1);
        x2 -= w;
        if (r.splitter) splitAt(r, x2, y1, SPLIT, y2 - y1);
        if (!r.opts.split && r.opts.border !== false) x2 += 1;
      }
    }
    if (R.west) {
      const r = R.west;
      if (r.collapsed && !r.peeking) { placeBar(r, 0, y1, r.opts.collapsedSize, y2 - y1); x1 += r.opts.collapsedSize; }
      else if (!r.collapsed) {
        const w = Layout.resolveSize(r.opts.width, W);
        place(r, x1, y1, w, y2 - y1);
        x1 += w;
        if (r.splitter) splitAt(r, x1 - SPLIT, y1, SPLIT, y2 - y1);
        if (!r.opts.split && r.opts.border !== false) x1 -= 1;
      }
    }
    if (R.center && !R.center.collapsed) {
      place(R.center, x1, y1, x2 - x1, y2 - y1);                          // exact residual; a missing center is fine
    }
    return this;                                            // mutators return `this`
  }

  /** relayout the direct nested layouts (they cascade further themselves) */
  _cascade() {
    if (!this.el.querySelectorAll) return;
    for (const n of this.el.querySelectorAll('.ics-layout')) {
      const anc = n.parentElement && n.parentElement.closest && n.parentElement.closest('.ics-layout');
      if (anc !== this.el) continue;
      const inst = Component.get(n);
      if (inst instanceof Layout) { inst.setSize(); inst._cascade(); }
    }
  }

  _applyInitialCollapsed() {                                              // the MARKUP collapsed states re-apply
    for (const dir of ['east', 'west', 'north', 'south']) {
      const rec = this._regions[dir];
      if (rec && rec.markupCollapsed && !rec.collapsed) this.collapse(dir, false);
    }
  }

  /* ---- collapse / expand / peek ---------------------------------------------- */

  _offPos(rec) {
    const w = rec.wrap.offsetWidth, h = rec.wrap.offsetHeight;
    if (rec.dir === 'west') return { prop: 'left', off: -w, on: 0 };
    if (rec.dir === 'east') return { prop: 'left', off: this._geom.W, on: this._geom.W - w };
    if (rec.dir === 'north') return { prop: 'top', off: -h, on: 0 };
    return { prop: 'top', off: this._geom.H, on: this._geom.H - h };
  }

  _ensureBar(rec) {
    if (rec.bar) return;
    const bar = document.createElement('div');
    bar.className = 'layout-expand layout-expand-' + rec.dir;
    bar.style.display = 'none';
    const a = document.createElement('a');
    a.href = 'javascript:void(0)';
    a.className = 'layout-button-' + BAR_ARROW[rec.dir];
    bar.appendChild(a);
    this.el.appendChild(bar);
    rec.bar = bar;
    this._events.add(bar, 'mouseenter', () => bar.classList.add('layout-expand-over'));   // hover paint
    this._events.add(bar, 'mouseleave', () => bar.classList.remove('layout-expand-over'));
    this._events.add(a, 'click', (ev) => { ev.stopPropagation(); this.expand(rec.dir); }); // icon = permanent
    this._events.add(bar, 'click', () => this._peek(rec));                                 // body = peek
  }

  /** is a dropdown open? either a combo panel this component does not own, or
      an ics popup - both suppress the peek re-collapse */
  _comboOpen() {
    const legacy = document.querySelector('body > div.combo-p > div.combo-panel');
    if (legacy && legacy.offsetParent !== null) return true;
    for (const p of document.querySelectorAll('.ics-popup')) {
      if (p.offsetParent !== null) return true;
    }
    return false;
  }

  _peek(rec) {
    if (!rec.collapsed || rec.peeking) return;
    rec.peeking = true;
    const g = this._geom, $ = jq();
    rec.wrap.classList.add('ics-layout-peek');
    rec.wrap.style.display = '';
    if (rec.dir === 'west' || rec.dir === 'east') {
      rec.wrap.style.top = g.top + 'px';
      rec.wrap.style.height = (g.bottom - g.top) + 'px';
    } else {
      rec.wrap.style.left = '0px';
      rec.wrap.style.width = g.W + 'px';
    }
    const pos = this._offPos(rec);
    rec.wrap.style[pos.prop] = pos.off + 'px';
    if ($) $(rec.wrap).stop(true, true).animate({ [pos.prop]: pos.on }, 'fast');
    else rec.wrap.style[pos.prop] = pos.on + 'px';

    const leave = () => {
      if (!rec.peeking) return;                            // expanded permanently meanwhile
      if (this._dragging || this._comboOpen()) {           // re-check at the NEXT mouseleave
        this._events.add(rec.wrap, 'mouseleave', leave, { once: true });
        return;
      }
      rec.peeking = false;
      const done = () => { rec.wrap.style.display = 'none'; rec.wrap.classList.remove('ics-layout-peek'); this.setSize(); };
      if ($) $(rec.wrap).stop(true, true).animate({ [pos.prop]: pos.off }, 'fast', done);
      else done();
    };
    this._events.add(rec.wrap, 'mouseleave', leave, { once: true });
  }

  /** collapse(region[, animate=true]) - center never collapses; a missing region is a no-op */
  collapse(dir, animate) {
    const rec = this._regions[dir];
    if (!rec || rec.collapsed || dir === 'center') return this;
    this._ensureBar(rec);
    rec.collapsed = true;                                  // the state flips SYNCHRONOUSLY
    rec.opts.collapsed = true;                             // truthful bag - shell code reads it
    rec.peeking = false;
    rec.wrap.classList.remove('ics-layout-peek');
    if (rec.splitter) rec.splitter.style.display = 'none';
    const $ = jq();
    const pos = this._offPos(rec);
    const done = () => {
      if (!rec.collapsed) return;                          // an expand raced us - the last call wins
      rec.wrap.style.display = 'none';
      this.setSize(); this._cascade();
    };
    this.setSize();                                        // bar appears + neighbors absorb NOW
    this._cascade();
    if (animate !== false && $) $(rec.wrap).stop(true, true).animate({ [pos.prop]: pos.off }, done);
    else done();
    return this;
  }

  /** expand(region[, animate=true]) - a NO-OP unless the expand bar exists */
  expand(dir, animate) {
    const rec = this._regions[dir];
    if (!rec || !rec.bar || !rec.collapsed) return this;
    rec.collapsed = false;
    rec.opts.collapsed = false;
    rec.peeking = false;
    rec.bar.style.display = 'none';
    rec.wrap.classList.remove('ics-layout-peek');
    rec.wrap.style.display = '';
    const $ = jq();
    const pos = this._offPos(rec);
    const done = () => {
      if (rec.collapsed) return;                           // a collapse raced us
      this.setSize(); this._cascade();
    };
    if (animate !== false && $) {
      rec.wrap.style[pos.prop] = pos.off + 'px';
      $(rec.wrap).stop(true, true).animate({ [pos.prop]: pos.on }, done);
    } else done();
    return this;
  }

  /** isCollapsed(region) - the region's live state (false for a missing region);
      the shell's sidebar toggle reads THIS rather than the region panel's option
      bag, because the panel dispatcher may belong to another bundle */
  isCollapsed(dir) {
    const rec = this._regions[dir];
    return !!(rec && rec.collapsed);
  }

  /* ---- splitter drag ----------------------------------------------------------- */

  _makeSplitter(rec) {
    const horiz = rec.dir === 'north' || rec.dir === 'south';
    const s = document.createElement('div');
    s.className = 'ics-layout-splitter ' + (horiz ? 'ics-layout-splitter-h' : 'ics-layout-splitter-v');
    s.style.display = 'none';
    this.el.appendChild(s);
    this._events.add(s, 'pointerdown', (ev) => this._dragStart(rec, ev));
    return s;
  }

  _dragStart(rec, ev) {
    ev.preventDefault();
    this._dragging = true;                                 // suppresses the peek re-collapse
    const horiz = rec.dir === 'north' || rec.dir === 'south';
    const start = horiz ? ev.clientY : ev.clientX;
    const startSize = horiz ? rec.wrap.offsetHeight : rec.wrap.offsetWidth;
    const min = horiz ? rec.opts.minHeight : rec.opts.minWidth;
    const max = horiz ? rec.opts.maxHeight : rec.opts.maxWidth;
    const g = this._geom;
    const mask = document.createElement('div');
    mask.className = 'layout-mask';                        // blocks iframes/hover during the drag
    this.el.appendChild(mask);
    const proxy = horiz ? this._proxyH : this._proxyV;
    proxy.style.display = '';
    if (horiz) { proxy.style.left = '0px'; proxy.style.width = g.W + 'px'; }
    else { proxy.style.top = g.top + 'px'; proxy.style.height = (g.bottom - g.top) + 'px'; }

    const placeProxy = (size) => {
      if (rec.dir === 'north') proxy.style.top = (rec.wrap.offsetTop + size - 3) + 'px';
      else if (rec.dir === 'south') proxy.style.top = (rec.wrap.offsetTop + rec.wrap.offsetHeight - size) + 'px';
      else if (rec.dir === 'west') proxy.style.left = (rec.wrap.offsetLeft + size - 3) + 'px';
      else proxy.style.left = (rec.wrap.offsetLeft + rec.wrap.offsetWidth - size) + 'px';
    };
    placeProxy(startSize);
    rec._dragSize = startSize;

    const move = (e) => {
      let d = (horiz ? e.clientY : e.clientX) - start;
      if (rec.dir === 'south' || rec.dir === 'east') d = -d;
      rec._dragSize = Math.min(max, Math.max(min, startSize + d));       // clamped to min/max
      placeProxy(rec._dragSize);
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      proxy.style.display = 'none';
      mask.remove();
      this._dragging = false;
      if (rec._dragSize != null) {
        if (horiz) rec.opts.height = rec._dragSize;        // px overwrites '%': proportional sizing ends here
        else rec.opts.width = rec._dragSize;
        rec._dragSize = null;
      }
      this.setSize(); this._cascade();
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up, { once: true });
  }

  /* ---- API surface ---------------------------------------------------------------
     collapse/expand are above; adding/removing a region is not implemented. */

  /** recompute geometry; an optional {width,height} merges into the container */
  resize(param) {
    if (param && typeof param === 'object') {
      if (param.width != null) this.el.style.width = typeof param.width === 'number' ? param.width + 'px' : param.width;
      if (param.height != null) this.el.style.height = typeof param.height === 'number' ? param.height + 'px' : param.height;
    }
    this.setSize(); this._cascade();
    return this;
  }

  /** the no-argument call: re-measure, then re-apply the MARKUP collapsed states */
  doLayout() {
    this.setSize();
    this._applyInitialCollapsed();
    this._cascade();
    return this;
  }

  /** layout('panel', region) - the region panel wrapped in jQuery, so callers
      can chain .panel('options').collapsed; a missing region answers an EMPTY
      set and never throws */
  panel(dir) {
    const rec = this._regions[dir];
    const $ = jq();
    if ($) return $(rec ? rec.inst.el : []);
    return rec ? rec.inst.el : null;
  }

  _optionChanged(name) {
    if (name === 'fit' || name === 'width' || name === 'height') this.setSize();
  }

  _destroy() {
    for (const dir of Object.keys(this._regions)) {
      const rec = this._regions[dir];
      if (rec.bar) rec.bar.remove();
      if (rec.splitter) rec.splitter.remove();
      rec.inst.destroy();
      rec.body.classList.remove('layout-body');
    }
    this._regions = {};
    if (this._proxyH) this._proxyH.remove();
    if (this._proxyV) this._proxyV.remove();
    this.el.classList.remove('layout', 'ics-layout');
  }
}

export const LAYOUT_OWN = '[data-ics="layout"]';

/** register the component in THIS bundle's Registry - called by the ENTRY
    (index.layout.js) and by the harness root (index.js); never at import, so a
    bundle importing this module for LayoutService alone registers nothing */
export function registerLayout() {
  if (Registry.get('Layout')) return Registry.get('Layout');
  Registry.add('Layout', Layout, {
    selector: LAYOUT_OWN,
    claimName: 'layout',                 // $(el).layout(...) plugin name (claim-if-free; the entry dispatches)
  });
  return Registry.get('Layout');
}

/* =============================================================================
   THE APP SHELL.

   _Layout.cshtml renders TWO ways:
     SHELL render (a top-level navigation)  = the sidebar + a same-origin
       <iframe id="icsScreenFrame"> at the request's own URL (area 2);
     FRAME render (Sec-Fetch-Dest: iframe, or the `icsframe=1` marker) = the
       screen alone.
   The .cshtml emits `window.IcsShellConfig` (the Razor URLs + the session
   values, see CONFIG_DEFAULTS) before the body markup; shell.boot() runs at
   ready, reads it, and does per render:
     common  ProgressBarHide, the tab title, the user name, LoadBasicEventsFromLayout,
             the backspace guard, #btnHome
     shell   mount the own layout, assign the frame's src AFTER the mount (a
             moved iframe reloads), build the menu tree (ics-tree) + its remembered
             open-folder set + scroll position, the welcome notice (IcsMsgBox),
             the white-label copyright flag, the frame load sync (replaceState —
             never pushState — title, lit menu item), the
             nested-shell degrade for a browser without Sec-Fetch-Dest
     frame   the shared mail window's handlers
   The PAGE GLOBALS (SHELL_GLOBALS) are attached at bundle load on every page
   that renders _Meta; a shell that defines its own copy later (_MobileLayout's
   icsNavigateTo) still wins, because a later inline script overwrites a global.
   ============================================================================= */
const CONFIG_DEFAULTS = {
  framed: false,
  menuId: null,
  pageTitle: 'ESimSol ERP',
  dynamicTitle: null,
  companyName: null,
  isAutoHideMenuList: null,
  currentUser: null,          // JSON text (the session stores the serialised User)
  menu: null,                 // JSON text (Session.Menu) - the INLINE form, honoured when a shell emits it
  menuVersion: null,          // the session menu's content hash: the shell FETCHES urls.shellMenu once per version
  accountLicense: null,       // JSON text (Session.AccountLicense)
  frameSrc: null,
  urls: {
    shellMenu: '/Home/GetShellMenu',
    getMaskedUrl: '/UrlMasking/GetMaskedUrl',
    getUrlByToken: '/UrlMasking/GetUrlByToken',
    pdfViewer: '/PdfViewer/Show',
    setNotification: '/HumanInteractionAgent/SetNotification',
    getNewNotification: '/HumanInteractionAgent/GetNewNotification',
    updateRead: '/HumanInteractionAgent/UpdateRead',
    copyrightHide: '/Home/GetIsCopyWriteInfoHide',
    removeLicenseSession: '/Company/RemoveIcsAccountLicenseSession',
    home: '/Home/ViewBackToHomeDashBoard?menuid=0',
    okCheckIcon: '/lib-v2/ics-icons/svg/ok-check.svg',
  },
};

/** the live config: the page's window.IcsShellConfig over the defaults (read at every call) */
export function shellConfig() {
  const g = globalThis;
  const c = g.IcsShellConfig || {};
  return Object.assign({}, CONFIG_DEFAULTS, c, { urls: Object.assign({}, CONFIG_DEFAULTS.urls, c.urls || {}) });
}

function parseJsonText(t) {
  if (t == null || t === '') return null;
  if (typeof t !== 'string') return t;
  try { return JSON.parse(t); } catch (e) { return null; }
}

/* ---- THE FRAME MARKER -----------------------------------------------------------
   Browsers send Sec-Fetch-* headers ONLY to secure contexts (https / localhost); on a
   plain http://<host> deployment the server cannot see `Sec-Fetch-Dest: iframe`, and a
   frame answered with a SHELL would render the sidebar twice. So every URL the shell
   loads into its frame, and every same-tab navigation a framed screen makes, carries
   `icsframe=1`; _Layout and ShellRenderFilter read it as the frame witness, the header
   as the second one. The address bar never shows it (onFrameLoad strips it). */
const FRAME_MARK = 'icsframe';
function withFrameMark(url) {
  if (!url || /^(about:|javascript:|#)/i.test(url)) return url;
  try {
    const u = new URL(url, globalThis.location.href);
    if (u.origin !== globalThis.location.origin) return url;
    if (u.searchParams.get(FRAME_MARK) === '1') return url;
    u.searchParams.set(FRAME_MARK, '1');
    return u.pathname + u.search + u.hash;
  } catch (e) { return url; }
}
function stripFrameMark(rel) {
  try {
    const u = new URL(rel, globalThis.location.href);
    if (!u.searchParams.has(FRAME_MARK)) return rel;
    u.searchParams.delete(FRAME_MARK);
    return u.pathname + u.search + u.hash;
  } catch (e) { return rel; }
}

/* ---- the page globals -------------------------------------------------------- */

/** every same-tab destination funnels through here: the SCREEN FRAME when this
    document is the shell, this document's own location (marked as a frame) when it is the framed screen */
function icsSameTabGo(url) {
  const g = globalThis;
  if (g.IcsShell && shell.frame) shell.navigate(url);
  else g.location.href = g.IcsFramed ? withFrameMark(url) : url;
}

function icsNavigateTo(url, target) {
  const g = globalThis, $ = jq(), cfg = shellConfig();
  if (!url || url.trim() === '' || url.trim() === '#') return;
  const pathOnly = url.split('?')[0];
  const queryString = url.includes('?') ? url.split('?')[1] : '';
  const segments = pathOnly.startsWith('/') ? pathOnly.substring(1).split('/') : pathOnly.split('/');
  if (segments.length < 2 || !$) {
    if (target === '_blank') { g.open(url); } else { icsSameTabGo(url); }
    return;
  }
  const controllerName = segments[0];
  const actionName = segments[1];
  const friendlyTitle = actionName;
  const routeValues = {};
  const params = new URLSearchParams(queryString);
  for (const [key, value] of params.entries()) { routeValues[key] = value; }
  $.ajax({
    url: cfg.urls.getMaskedUrl,
    type: 'POST',
    contentType: 'application/json; charset=utf-8',
    data: JSON.stringify({ controller: controllerName, action: actionName, routeValues, friendlyTitle }),
    success(data) {
      if (!data || !data.token) {
        if (target === '_blank') { g.open(url); } else { icsSameTabGo(url); }
        return;
      }
      const token = data.token, type = data.type;
      const destinationUrl = '/' + token;
      if (target === '_blank') {
        if (type === 'download') {
          const link = document.createElement('a');
          link.style.display = 'none'; link.href = destinationUrl;
          const ext = friendlyTitle.toLowerCase().includes('excel') ? '.xlsx' : '.pdf';
          link.download = friendlyTitle + ext;
          document.body.appendChild(link); link.click(); document.body.removeChild(link);
          if (typeof g.IcsToastInfo === 'function') g.IcsToastInfo('Downloading ' + friendlyTitle, { title: 'Downloading!', timeout: 5000 });
        } else {
          const windowKey = 'window_' + new Date().getTime();
          const viewerUrl = cfg.urls.pdfViewer
            + '?title=' + encodeURIComponent(friendlyTitle)
            + '&pdfUrl=' + encodeURIComponent(destinationUrl)
            + '&windowKey=' + encodeURIComponent(windowKey);
          const newWin = g.open(viewerUrl, '_blank');
          if (newWin) { g.openedWindows = g.openedWindows || {}; g.openedWindows[windowKey] = newWin; }
        }
      } else if (target === '_new') {
        g.open(destinationUrl, '_blank');
      } else {
        icsSameTabGo(destinationUrl);
      }
    },
    error() {
      if (target === '_blank') { g.open(url); } else { icsSameTabGo(url); }
    },
  });
}

function icsCloseWindow() {
  const g = globalThis;
  const windowKey = sessionStorage.getItem('icsWindowKey');
  const openerWindow = g.top.opener;
  if (windowKey && openerWindow && !openerWindow.closed) {
    if (openerWindow.openedWindows && openerWindow.openedWindows[windowKey]) {
      openerWindow.openedWindows[windowKey].close();
      delete openerWindow.openedWindows[windowKey];
    } else { g.top.close(); }
  } else { g.top.close(); }
}

async function icsNevigateUrl() {
  const $ = jq(), cfg = shellConfig();
  const sToken = globalThis.location.href.split('/').pop();
  try {
    return await $.ajax({ url: cfg.urls.getUrlByToken, dataType: 'json', type: 'POST', traditional: true, contentType: 'application/json; charset=utf-8', data: JSON.stringify({ token: sToken }) });
  } catch (e) { return ''; }
}

function openMobileSidebar() {
  const $ = jq(); if (!$) return;
  $('#mobile-sidebar').addClass('open');
  $('#sidebar-overlay').fadeIn(200);
  $('body').css('overflow', 'hidden');
}
function closeMobileSidebar() {
  const $ = jq(); if (!$) return;
  $('#mobile-sidebar').removeClass('open');
  $('#sidebar-overlay').fadeOut(200);
  $('body').css('overflow', '');
}

/* notifications (the HIA feed) */
function SetNotification() {
  const $ = jq(), cfg = shellConfig(); if (!$) return;
  $.ajax({ type: 'POST', dataType: 'json', url: cfg.urls.setNotification, data: JSON.stringify(), contentType: 'application/json; charset=utf-8',
    success(data) { const n = data; const el = document.getElementById('notification_count'); if (el) el.innerHTML = parseInt(n) > 0 ? parseInt(n).toFixed() : ''; }, error() {} });
}
function GetNewNotification() {
  const $ = jq(), cfg = shellConfig(), g = globalThis; if (!$) return;
  $.ajax({ type: 'POST', dataType: 'json', url: cfg.urls.getNewNotification, data: JSON.stringify(), contentType: 'application/json; charset=utf-8',
    success(data) { const n = data; if (parseInt(n) > 0) { if (typeof g.IcsToastInfo === 'function') g.IcsToastInfo('You have ' + n + ' new notification(s)', { title: 'Notifications', onClick() { ShowAllNotification(); } }); SetNotification(); } }, error() {} });
}
function ShowAllNotification() { icsNavigateTo('/HumanInteractionAgent/ViewNotifications'); }
function DispalyNotification(oNotifications) {
  let sHTML = '';
  for (let i = 0; i < oNotifications.length; i++) {
    const bg = oNotifications[i].IsRead ? 'whitesmoke' : '#6fcd6f';
    sHTML += "<div style='background-color:" + bg + "' onclick='NitificationRead(" + oNotifications[i].HIAID + ")' class='notifications'><p>" + oNotifications[i].SetupName + ' ' + SetApprovedButton(oNotifications[i].HIAID, oNotifications[i].LinkReference) + '</p>' + oNotifications[i].MessageBodyText + '</div>';
  }
  return sHTML;
}
function SetApprovedButton(nHIAID, sLinkReference) {
  const cfg = shellConfig();
  if (sLinkReference !== '') return "<input type='image' width='16' height='16' src='" + cfg.urls.okCheckIcon + "' onclick='Approved(" + nHIAID + ")' value=''/>";
  return '';
}
function NitificationRead(nID) {
  const $ = jq(), cfg = shellConfig(), g = globalThis; if (!$) return;
  $.ajax({ type: 'POST', dataType: 'json', url: cfg.urls.updateRead, data: JSON.stringify({ HIAID: nID }), contentType: 'application/json; charset=utf-8', success() {}, error(x, s, e) { if (typeof g.IcsToastErr === 'function') g.IcsToastErr(e); } });
}
function Approved(nHIAID) {
  const oHIA = GetHIA(nHIAID); if (!oHIA) return;
  const tsv = ((new Date()).getTime()) / 1000;
  sessionStorage.setItem(oHIA.OperationName, oHIA.OperationValue);
  sessionStorage.setItem('BackLink', globalThis.location.href);
  const IsMasterLink = oHIA.LinkReference.split('?');
  if (IsMasterLink.length > 1) { icsNavigateTo('/' + oHIA.LinkReference, '_blank'); }
  else {
    let sDynamicLink = '/' + oHIA.LinkReference + '?';
    if (oHIA.Parameter !== '') {
      let bFlag = true; const oParms = oHIA.Parameter.split('~');
      for (let i = 0; i < oParms.length; i++) {
        const sTempParam = oParms[i].split('=');
        sDynamicLink += sTempParam[0] + '=';
        if (sTempParam[1] === 'RefObjID') { sDynamicLink += oHIA.OperationObjectID + '&'; }
        else if (sTempParam.length > 2 && sTempParam[2] === 'tsNotUse') { bFlag = false; sDynamicLink += sTempParam[1]; }
        else { sDynamicLink += sTempParam[1] + '&'; }
      }
      if (bFlag) { sDynamicLink += 'ts=' + tsv; }
      icsNavigateTo(sDynamicLink);
    } else if (parseInt(oHIA.OrderStepID) > 0) {
      icsNavigateTo('/TAPExecution/ViewNotificationTAP?nTAPID=0&nTaskCategory=1&nHIAID=' + oHIA.HIAID + '&nOrderStepID=' + oHIA.OrderStepID + '&ts=' + tsv);
    }
  }
}
function GetHIA(nHIAID) {
  const list = globalThis._oNotifications || [];
  for (let i = 0; i < list.length; i++) { if (nHIAID === list[i].HIAID) return list[i]; }
  return null;
}

function readURL(input) {
  const $ = jq();
  if (input.files && input.files[0]) {
    const reader = new FileReader();
    reader.onload = (e) => { if ($) $('#imgCompanyLogo').attr('src', e.target.result).width(150).height(100); };
    reader.readAsDataURL(input.files[0]);
  }
}

/* LOGOUT: POST /Auth/Logout - the server revokes the refresh token and deletes the httpOnly jwt
   cookies + the session cookie - then the sign-in page, whatever the POST answered (a dead server
   must still land the user on the login form). The JWT lives in httpOnly cookies the bearer handler
   reads (Program.cs OnMessageReceived); there is no client-side token store. Runs in the SHELL: from
   a framed screen it takes the whole tab. */
function performLogout() {
  const $ = jq();
  const go = () => { const w = globalThis; (w.top || w).location.href = '/Auth/Login'; };
  if (!$) { go(); return; }
  $.ajax({ url: '/Auth/Logout', type: 'POST', complete: go });
}

/** white-label: the Home/GetIsCopyWriteInfoHide flag hides the copyright foot + the company span */
function IsCopyWriteInfoHide() {
  const $ = jq(), cfg = shellConfig(); if (!$) return;
  $.ajax({
    type: 'POST', dataType: 'json', url: cfg.urls.copyrightHide, data: {}, contentType: 'application/json; charset=utf-8',
    success(data) { if (data) $('#LinkICSInfo,#spanCompany').hide(); else $('#LinkICSInfo,#spanCompany').show(); },
    error() {},
  });
}

/** the page globals this module provides on every page (the identity list, for the gates) */
export const SHELL_GLOBALS = {
  icsSameTabGo, icsNavigateTo, icsCloseWindow, icsNevigateUrl,
  openMobileSidebar, closeMobileSidebar,
  SetNotification, GetNewNotification, ShowAllNotification, DispalyNotification, SetApprovedButton, NitificationRead, Approved, GetHIA,
  readURL, performLogout, IsCopyWriteInfoHide,
};

/** attach the page globals + the page-state variables the views read (last write wins) */
export function attachShellGlobals(g) {
  for (const k of Object.keys(SHELL_GLOBALS)) g[k] = SHELL_GLOBALS[k];
  if (!Array.isArray(g._oNotifications)) g._oNotifications = [];
  if (g._IsAutoHideMenuList === undefined) g._IsAutoHideMenuList = false;
  if (!Array.isArray(g._oMenuList)) g._oMenuList = [];
  if (g._oIcsAccountLicense === undefined) g._oIcsAccountLicense = null;
  if (g._currentUser === undefined) g._currentUser = null;
  return Object.keys(SHELL_GLOBALS).length;
}

/* ---- the shell object ----------------------------------------------------------- */

/** the sidebar auto-collapse breakpoint (px): below it the west region collapses; 1100 keeps the
    menu open on a 1366-wide laptop and collapses it on tablets / split screens */
export const SIDEBAR_COLLAPSE_BREAKPOINT = 1100;
const MENU_STATE_KEY = 'ics.menu.open';
const MENU_SCROLL_KEY = 'ics.menu.scrollTop';
/* THE MENU'S LOCAL SETTINGS: the search box is VISIBLE by default and its open/closed choice is the user's
   preference ('ics.menu.search:<user>' = 'open' | 'closed'); on the FIRST visit (no remembered open-folder
   set) the tree opens ONE level after the root - the root and its direct folders open, everything deeper
   closed. Both are the shell's rules applied on top of the tree's own API; the tree component itself
   carries neither. */
const MENU_SEARCH_KEY = 'ics.menu.search';
const MENU_CACHE_KEY = 'ics.menu.cache';   // sessionStorage: { v: menuVersion, menu } per user (loadMenu)
const menuSearchKey = () => MENU_SEARCH_KEY + ':' + (globalThis.IcsUserKey != null && globalThis.IcsUserKey !== '' ? String(globalThis.IcsUserKey) : 'anon');

export const shell = {
  frame: null,                 // the screen <iframe> (shell render only)
  pending: false,              // a shell-started navigation is in flight
  booted: false,

  frameWindow() { try { return this.frame ? this.frame.contentWindow : null; } catch (e) { return null; } },

  /** load a same-origin URL into area 2; the screen's own splash takes over once its
      document parses, this covers the server think-time before that */
  navigate(url) {
    const $ = jq();
    this.pending = true;
    try { if ($ && typeof $.icsProgressBar === 'function') $.icsProgressBar(true); } catch (e) { }
    this.frame.src = withFrameMark(url);
  },

  /** light the menu item a screen belongs to (the frame exposes window.IcsMenuID = Session.MenuID) */
  selectMenu(id) {
    const $ = jq(); if (!$) return;
    if (id == null || id === '' || isNaN(id)) return;
    const node = $('#tt').tree('find', id) || $('#tt').tree('find', parseInt(id, 10));
    if (!node || !node.target) return;
    const cur = $('#tt').tree('getSelected');
    if (cur && String(cur.id) === String(node.id)) return;
    $('#tt').tree('expandTo', node.target);
    $('#tt').tree('select', node.target);
  },

  /** every load of the frame - a menu click, an in-screen navigation, a redirect,
      Back/Forward - re-syncs the address bar (REPLACE, never push: the frame's navigation
      is already the history entry), the tab title and the lit menu item */
  onFrameLoad() {
    const $ = jq();
    this.pending = false;
    try { if ($ && typeof $.icsProgressBar === 'function') $.icsProgressBar(false); } catch (e) { }
    const w = this.frameWindow(); if (!w) return;
    let href, title, menuId;
    try { href = w.location.href; title = w.document.title; menuId = w.IcsMenuID; } catch (e) { return; }
    if (!href || href === 'about:blank') return;
    try {
      const u = new URL(href);
      const rel = stripFrameMark(u.pathname + u.search + u.hash);   // the marker is the frame's business, never the address bar's
      if (rel !== location.pathname + location.search + location.hash) history.replaceState(null, '', rel);
    } catch (e) { }
    if (title) document.title = title;
    this.selectMenu(menuId);
    try { w.focus(); } catch (e) { }
  },

  /* the menu tree ---------------------------------------------------------- */

  rewriteMenu(oUserMenu) {
    if (oUserMenu.attributes != null && oUserMenu.attributes !== '') {
      const segments = oUserMenu.attributes.split('~');
      const ControllerName = segments[0];
      const ActionName = segments[1];
      if (ActionName !== 'aaa') {
        const sparam = ActionName.indexOf('?') > -1 ? '&' : '?';
        let rawInternalUrl = '';
        if (!oUserMenu.IsActive) {
          const sMessage = oUserMenu.SpecialNote || 'You do not have enough permission to access this menu. Please contact with vendor.';
          const tsv = ((new Date()).getTime()) / 1000;
          rawInternalUrl = '/Home/ViewErrorMessage?ts=' + tsv + '&ErroMessage=' + encodeURIComponent(sMessage);
        } else {
          rawInternalUrl = '/' + ControllerName + '/' + ActionName + sparam;
          if (oUserMenu.IsWithBU) rawInternalUrl += 'buid=' + oUserMenu.BUID + '&menuid=' + oUserMenu.id;
          else rawInternalUrl += 'menuid=' + oUserMenu.id;
        }
        // the real URL on the anchor shows in the status bar (developer feature); the click is intercepted
        oUserMenu.text = '<a href="' + rawInternalUrl + '" data-url="' + rawInternalUrl + '">' + oUserMenu.text + '</a>';
      }
    }
    if (oUserMenu.children != null && oUserMenu.children.length > 0) {
      for (let i = 0; i < oUserMenu.children.length; i++) oUserMenu.children[i] = this.rewriteMenu(oUserMenu.children[i]);
    }
    return oUserMenu;
  },

  refreshMenuObj(oMenu) {
    const $ = jq();
    let sState = oMenu.state;
    const domstate = $ ? $('#tt').data('state') : null;
    if (domstate != null) sState = domstate;
    return { id: oMenu.id, parentid: oMenu.parentid, text: oMenu.text, state: sState, attributes: oMenu.attributes, IsWithBU: oMenu.IsWithBU, BUID: oMenu.BUID, IsActive: oMenu.IsActive, ModuleName: oMenu.ModuleName, ActivityInString: oMenu.ActivityInString, ControllerName: oMenu.ControllerName, ActionName: oMenu.ActionName, Sequence: oMenu.Sequence, SpecialNote: oMenu.SpecialNote, children: [] };
  },

  collectChildren(oMenu, list) {
    const childList = oMenu.children || [];
    for (let i = 0; i < childList.length; i++) {
      const oChild = childList[i];
      list.push(this.refreshMenuObj(oChild));
      if (oChild.children && oChild.children.length > 0) this.collectChildren(oChild, list);
    }
  },

  buildMenuTree(oUserMenu) {
    const $ = jq(), g = globalThis, self = this;
    oUserMenu = this.rewriteMenu(oUserMenu);
    $('#tt').tree({
      data: [oUserMenu],
      onClick(node) {
        const anchor = $(node.target).find('a[data-url]');
        const targetUrl = anchor.attr('data-url');
        if (targetUrl) {
          /* clicked from a search short-list: the remembered scrollTop belongs to the FILTERED tree */
          if (String($('#tt .ics-tree__search').val() || '').trim() !== '') {
            try { localStorage.removeItem(MENU_SCROLL_KEY); } catch (e) { }
          }
          sessionStorage.clear();                                  // old page state (shared with the framed screen)
          sessionStorage.setItem('SelectedRowIndex', -1);
          sessionStorage.setItem('MenuID', node.id);
          sessionStorage.setItem('BUID', node.BUID);
          sessionStorage.setItem('IsSuperUser', g._currentUser ? g._currentUser.IsSuperUser : false);
          closeMobileSidebar();
          icsNavigateTo(targetUrl);                                // lands in the screen frame
        } else if (node.attributes) {
          const action = node.attributes.split('~')[1];
          if (action === 'aaa') {
            if (node.state === 'closed') $(this).tree('expand', node.target);
            else $(this).tree('collapse', node.target);
          }
        }
      },
    });
    /* a plain click on the anchor itself must not navigate THIS document - onClick handles it */
    $('#tt').off('click.icsFrameShell', 'a[data-url]').on('click.icsFrameShell', 'a[data-url]', (e) => { e.preventDefault(); });
    void self;
  },

  /* the menu's OPEN-FOLDER set + scroll position, remembered across page loads */
  menuFolders(nodes, out) {
    for (let i = 0; nodes && i < nodes.length; i++) {
      const n = nodes[i];
      if (n.children && n.children.length) { out.push(n); this.menuFolders(n.children, out); }
    }
    return out;
  },
  menuStateSave() {
    const $ = jq(); if (!$) return;
    try {
      const open = this.menuFolders($('#tt').tree('getData'), []).filter((n) => n.state !== 'closed').map((n) => n.id);
      localStorage.setItem(MENU_STATE_KEY, JSON.stringify(open));
    } catch (e) { }
  },
  menuStateRestore() {
    const $ = jq(); if (!$) return;
    let open = null;
    try { open = JSON.parse(localStorage.getItem(MENU_STATE_KEY) || 'null'); } catch (e) { }
    if (!Array.isArray(open)) { this.menuOpenFirstLevel(); return; }   // FIRST visit: one level after the root (an EMPTY remembered set is a choice and stands)
    const folders = this.menuFolders($('#tt').tree('getData'), []);
    for (const n of folders) {
      const want = open.indexOf(n.id) > -1;
      if (want && n.state === 'closed') $('#tt').tree('expand', n.target);
      else if (!want && n.state !== 'closed') $('#tt').tree('collapse', n.target);
    }
  },
  /** the first-visit shape: the root open, its direct folders open, every deeper folder closed */
  menuOpenFirstLevel() {
    const $ = jq(); if (!$) return;
    const roots = $('#tt').tree('getData') || [];
    const setOpen = (n, want) => {
      if (!n.children || !n.children.length) return;
      if (want && n.state === 'closed') $('#tt').tree('expand', n.target);
      else if (!want && n.state !== 'closed') $('#tt').tree('collapse', n.target);
    };
    const walk = (nodes, depth) => {
      for (const n of nodes || []) { setOpen(n, depth <= 1); walk(n.children, depth + 1); }
    };
    walk(roots, 0);
  },
  /** the search box preference: visible unless the user closed it; the chip / Escape record the choice */
  menuSearchPref() {
    const $ = jq(); if (!$) return;
    const el = document.getElementById('tt');
    const inst = el && globalThis.Ics && globalThis.Ics.Component ? globalThis.Ics.Component.get(el) : null;
    if (!inst || typeof inst.toggleSearch !== 'function') return;
    const isOpen = () => !!el.querySelector('.ics-tree__searchrow--open');
    let pref = null;
    try { pref = localStorage.getItem(menuSearchKey()); } catch (e) { }
    const want = pref !== 'closed';
    if (want !== isOpen()) {
      inst.toggleSearch(want);
      if (want) { const box = el.querySelector('.ics-tree__search'); if (box && typeof box.blur === 'function') box.blur(); }   // visible, but the boot never steals the focus
    }
    /* the user's later flips (the chip, Escape) are RECORDED by watching the box's own open class - the tree's
       chip handler stops propagation, so no delegated listener would see the click; the observer is armed
       AFTER the boot's own toggle above, so the default is never written as a preference */
    const row = el.querySelector('.ics-tree__searchrow');
    if (row && globalThis.MutationObserver) {
      if (this._menuSearchObs) this._menuSearchObs.disconnect();
      setTimeout(() => {
        this._menuSearchObs = new MutationObserver(() => { try { localStorage.setItem(menuSearchKey(), isOpen() ? 'open' : 'closed'); } catch (e) { } });
        this._menuSearchObs.observe(row, { attributes: true, attributeFilter: ['class'] });
      }, 0);
    }
  },
  menuScrollMemory() {
    const $ = jq();
    const box = document.getElementById('menuScroll');
    if (!box) return;
    try {
      const st = localStorage.getItem(MENU_SCROLL_KEY);
      if (st != null && st !== '') box.scrollTop = parseInt(st, 10) || 0;
    } catch (e) { }
    box.addEventListener('scroll', () => { try { localStorage.setItem(MENU_SCROLL_KEY, String(box.scrollTop)); } catch (e) { } }, { passive: true });
    if ($) $('#tt').on('click', () => { setTimeout(() => this.menuStateSave(), 0); });
  },

  /** the account-license welcome notice: an own message box (IcsMsgBox), plain text only */
  welcomeNotice(license) {
    const g = globalThis, $ = jq(), cfg = shellConfig();
    if (!license || !license.IsShowNotice) return;
    let text = String(license.NotifyMessage || 'Welcome to the Summary Dashboard!');
    try { const d = new DOMParser().parseFromString(text, 'text/html'); text = (d.body.textContent || '').trim() || text; } catch (e) { }
    const done = () => { if ($) $.ajax({ type: 'GET', url: cfg.urls.removeLicenseSession, dataType: 'json', contentType: 'application/json; charset=utf-8', success() {}, error() {} }); };
    if (typeof g.IcsMsgBox === 'function') g.IcsMsgBox('Notice', text, done);
    else done();
  },

  /* boot ------------------------------------------------------------------- */

  bootCommon($, cfg) {
    const g = globalThis;
    if (typeof g.ProgressBarHide === 'function') g.ProgressBarHide();
    g._currentUser = parseJsonText(cfg.currentUser);
    const viewBagTitle = cfg.pageTitle || 'ESimSol ERP';
    $('title').text(viewBagTitle + (cfg.dynamicTitle || ''));
    $('#appPageTitle').text(viewBagTitle);
    if (g._currentUser && g._currentUser.UserName) $('#lblUserName').text(g._currentUser.UserName);
    g.addEventListener('load', () => { setTimeout(() => { g.dispatchEvent(new Event('resize')); }, 100); });
    if (typeof g.LoadBasicEventsFromLayout === 'function') g.LoadBasicEventsFromLayout();
  },

  bootShellRender($, cfg) {
    const g = globalThis;
    /* the own layout is mounted by the entry's auto-mount pass (before this ready handler);
       mount() here is the idempotent belt-and-braces. THEN the first screen's URL is assigned:
       the mount MOVES the region content into panel wrappers and a moved iframe reloads. */
    /* A SHELL INSIDE A FRAME NEVER NESTS: when the server has rendered a shell for a framed request - a
       context that sends no Sec-Fetch-Dest (plain http) and a URL that carried no marker (a raw location.href
       in a screen, a redirect that dropped the query, a modal child) - reload THIS document as a screen,
       marked. One extra request; never a second sidebar. */
    if (g.self !== g.top) {
      const marked = withFrameMark(location.pathname + location.search + location.hash);
      if (marked !== location.pathname + location.search + location.hash) { location.replace(marked); return; }
      document.documentElement.classList.add('ics-nested-shell');   // marked and still a shell = the server ignored the marker; degrade, never loop
    }
    const lay = document.getElementById('mainLayout');
    if (lay && !Component.get(lay) && lay.matches(LAYOUT_OWN)) { try { new Layout(lay); } catch (e) { console.error(e); } }
    this.frame = document.getElementById('icsScreenFrame');
    if (this.frame) {
      this.frame.addEventListener('load', () => this.onFrameLoad());
      if (!this.frame.getAttribute('src')) this.frame.src = withFrameMark(this.frame.getAttribute('data-src') || cfg.frameSrc || 'about:blank');
    }

    g._IsAutoHideMenuList = cfg.isAutoHideMenuList;
    g._oIcsAccountLicense = parseJsonText(cfg.accountLicense);
    const menuid = cfg.menuId;

    this.welcomeNotice(g._oIcsAccountLicense);

    if (cfg.companyName) {
      $('#CompanyNameBU').text(cfg.companyName);
      $('#spanCompany').text(cfg.companyName);
      $('#mobileCompanyName').text(cfg.companyName);
    }

    $('#tt').data('state', null);
    /* THE MENU IS FETCHED ONCE PER VERSION: the shell document does not inline the menu JSON (inlining it
       would make every top-level navigation a multi-megabyte response). An inline `cfg.menu` is honoured
       when a shell chooses to emit one. */
    this.loadMenu(cfg).then((oUserMenu) => this.applyMenu($, g, oUserMenu, menuid));
    this.menuScrollMemory();

    if (g._IsAutoHideMenuList === 'true' || g._IsAutoHideMenuList === true) sessionStorage.setItem('menucollapse', 'true');
    if (sessionStorage.getItem('menucollapse') === 'true') $('#app-sidebar').addClass('collapsed');

    IsCopyWriteInfoHide();
    this.bindShellChrome($, cfg);
  },

  /** the menu tree from a parsed menu object: build, restore the open-folder set + search preference,
      select the current menu item, then announce `ics:menuready` on window */
  applyMenu($, g, oUserMenu, menuid) {
    if (oUserMenu == null) return;
    g._oMenuList = [this.refreshMenuObj(oUserMenu)];
    this.collectChildren(oUserMenu, g._oMenuList);
    this.buildMenuTree(oUserMenu);
    /* the tree's own root tools (collapse all / expand all / search) change every folder at once */
    $('#tt').off('ics:treestate.icsMenu').on('ics:treestate.icsMenu', () => this.menuStateSave());
    this.menuStateRestore();
    this.menuSearchPref();
    if (menuid != null && menuid !== '' && !isNaN(menuid)) {
      const node = $('#tt').tree('find', menuid);
      if (node != null) { $('#tt').tree('expandTo', node.target); $('#tt').tree('select', node.target); }
    }
    try { g.dispatchEvent(new CustomEvent('ics:menuready')); } catch (e) { }
  },

  /** the menu source, in order: inline cfg.menu → sessionStorage copy of THIS version → urls.shellMenu.
      Resolves the parsed menu object or null (a failed fetch leaves the sidebar without a tree, never throws). */
  loadMenu(cfg) {
    const inline = parseJsonText(cfg.menu);
    if (inline != null) return Promise.resolve(inline);
    if (!cfg.menuVersion || !cfg.urls || !cfg.urls.shellMenu) return Promise.resolve(null);
    const key = MENU_CACHE_KEY + ':' + (globalThis.IcsUserKey != null ? globalThis.IcsUserKey : '');
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached && cached.v === cfg.menuVersion && cached.menu) return Promise.resolve(cached.menu);
      }
    } catch (e) { }
    return fetch(cfg.urls.shellMenu, { credentials: 'same-origin', headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' } })
      .then((r) => { if (!r.ok) throw new Error('shell menu ' + r.status); return r.json(); })
      .then((menu) => {
        try { sessionStorage.setItem(key, JSON.stringify({ v: cfg.menuVersion, menu })); } catch (e) { /* quota / private mode: re-fetch next time */ }
        return menu;
      })
      .catch((e) => { console.error('[ics-layout] shell menu load failed', e); return null; });
  },

  /** the sidebar chrome: the three popovers + the toast-history list (bindShellPopovers), the sidebar
      auto-collapse (bindSidebarCollapse), click containment inside the account popover, and the
      notification badge. The paint is ics-layout.css. This module persists no theme choice and sets
      no theme class on the body - the application has ONE theme. */
  bindShellChrome($, cfg) {
    this.bindShellPopovers($);
    this.bindSidebarCollapse($);
    /* a click inside the account popover on a NON-link row (the title) must not bubble to the .profile-btn
       toggle (bindShellPopovers) and close the popover; the links (Profile / Change Password / Logout)
       still bubble - they navigate or act anyway */
    $('#profileDropdown').on('click', (e) => { if (!$(e.target).closest('a').length) e.stopPropagation(); });
    /* the notification badge (#notification_count): the server's count for this user, filled at shell
       boot and left empty when the count is zero */
    if (cfg.urls && cfg.urls.setNotification && document.getElementById('notification_count')) SetNotification();
  },

  /** THE THREE POPOVERS of the user panel (_Sidebar): the account block (.profile-btn), the
      notification bell and the 'info' recall chip (both .bell-btn) - ONE open at a time, a document
      click closes them; plus the RECENT ALERTS list: the last 10 toasts (Ics.toastHistory, ics-messager.js;
      per user, persistent) repainted on open, on every new toast while open, and Clear. */
  bindShellPopovers($) {
    if (!$) return;
    $('.profile-btn').on('click', function (e) {
      e.stopPropagation();
      $('.bell-dropdown').hide();
      $(this).find('.profile-dropdown').toggle();
      $(this).toggleClass('open');
    });
    $('.bell-btn').on('click', function (e) {
      e.stopPropagation();
      $('.profile-dropdown').hide();
      $('.profile-btn').removeClass('open');
      /* ONE panel at a time: the footer bell and the 'info' recall chip are both .bell-btn and overlap
         in the same corner, so opening one closes the other */
      const $d = $(this).closest('.notification-area').find('.bell-dropdown');
      $('.bell-dropdown').not($d).hide();
      $d.toggle();
    });
    $(document).on('click', () => {
      $('.profile-btn').removeClass('open');
      $('.profile-dropdown, .bell-dropdown').hide();
    });
    const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const when = (t) => {
      const d = new Date(t), now = new Date();
      const hm = ('0' + d.getHours()).slice(-2) + ':' + ('0' + d.getMinutes()).slice(-2);
      if (d.toDateString() === now.toDateString()) return hm;
      return d.getDate() + ' ' + ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()] + ' ' + hm;
    };
    const render = () => {
      const $body = $('#toastHistoryBody');
      if (!$body.length) return;
      const api = globalThis.Ics && globalThis.Ics.toastHistory;
      const list = api ? api.list() : [];
      if (!list.length) { $body.html('<div class="toast-history-empty">No recent alerts</div>'); return; }
      const types = (api && api.types) || {};
      let html = '';
      for (const e of list) {
        const meta = types[e.type] || types.info || { icon: 'info' };
        html += '<div class="toast-history-item toast-history-item--' + esc(e.type) + '">'
          + '<i class="toast-history-icon ics-icon-' + esc(meta.icon) + '" aria-hidden="true"></i>'
          + '<div class="toast-history-text">'
          + '<div class="toast-history-title">' + esc(e.title) + '<span class="toast-history-when">' + when(e.t) + '</span></div>'
          + '<div class="toast-history-msg">' + esc(e.msg) + '</div>'
          + '</div></div>';
      }
      $body.html(html);
    };
    this.renderToastHistory = render;
    $('#btnToastHistory').on('click', () => { render(); });      // after the .bell-btn toggle above
    $('#lnkToastHistoryClear').on('click', (e) => {
      e.preventDefault(); e.stopPropagation();
      if (globalThis.Ics && globalThis.Ics.toastHistory) globalThis.Ics.toastHistory.clear();
      render();
    });
    $('.toast-history-dropdown').on('click', (e) => { e.stopPropagation(); });   // clicks inside keep it open
    document.addEventListener('ics:toast', () => { if ($('.toast-history-dropdown').is(':visible')) render(); });
  },

  /** THE SIDEBAR AUTO-COLLAPSE: the #sidebarToggle chip, a menu click / a click outside the sidebar
      below the breakpoint, and the debounced window resize drive the own layout's west region;
      sessionStorage 'menucollapse' (written 'true' by dashboard screens, and by the shell when
      _IsAutoHideMenuList is set) keeps it collapsed on a wide screen. The breakpoint is
      SIDEBAR_COLLAPSE_BREAKPOINT (1100px). Every path asks the layout instance and no-ops without one. */
  bindSidebarCollapse($) {
    if (!$) return;
    const collapseBreakpoint = SIDEBAR_COLLAPSE_BREAKPOINT;
    const lay = () => { const el = document.getElementById('mainLayout'); const inst = el && Component.get(el); return (inst instanceof Layout) ? inst : null; };
    const collapsed = (l) => l.isCollapsed('west') === true;
    const narrow = () => $(window).width() < collapseBreakpoint;
    const saved = () => { try { return sessionStorage.getItem('menucollapse') === 'true'; } catch (e) { return false; } };
    const onResize = () => {
      const l = lay(); if (!l) return;
      const c = collapsed(l);
      if (narrow() && !c) l.collapse('west');
      else if (!narrow() && c) l.expand('west');
      else if (saved() && !c) l.collapse('west');
      else if (!saved() && c) l.expand('west');
    };
    $('#sidebarToggle').on('click', () => { const l = lay(); if (!l) return; if (collapsed(l)) l.expand('west'); else l.collapse('west'); });
    /* dual selector: .ics-tree__row is the ics-tree node row; .tree-title a node row of a tree
       implementation this component does not own */
    $('#tt').on('click', '.tree-title, .ics-tree__row', () => { const l = lay(); if (l && narrow()) l.collapse('west'); });
    let timer;
    $(window).on('resize', () => { clearTimeout(timer); timer = setTimeout(onResize, 200); });
    $(document).on('click touchstart', (e) => {
      if (!narrow()) return;
      if ($(e.target).closest('#west-panel, #sidebarToggle').length) return;
      const l = lay(); if (l && !collapsed(l)) l.collapse('west');
    });
  },

  bootFrameRender($) {
    const g = globalThis;
    /* the shared mail window */
    $('#btnTriggerMail').click(() => {
      if (!$('#txtMailTo').val()) { if (typeof g.IcsAlert === 'function') g.IcsAlert('Please provide receiver email address.'); return; }
      const oManualMail = { MSBody: $('#txtCC').val(), SenderEmail: $('#txtMailTo').val(), MSMailGenModuleInt: parseInt($('#winCommonMailSender').data('MailGenModule')) };
      if (typeof $.icsDataGet !== 'function') return;
      $.icsDataGet({ BaseAddress: '', Object: oManualMail, ControllerName: 'ManualMail', ActionName: 'SendMail', IsWinClose: false }, (response) => {
        if (response.status && response.obj != null) { if (typeof g.IcsAlert === 'function') g.IcsAlert(response.obj.ErrorMessage); $('#winCommonMailSender').icsWindow('close'); }
      });
    });
    $('#btnCancelMail').click(() => { $('#winCommonMailSender').icsWindow('close'); });
  },

  /** the backspace guard + the home shortcut: bound once per document */
  bindDocument($) {
    const cfg = shellConfig();
    $(document).unbind('keydown').bind('keydown', (event) => {
      if (event.keyCode === 8) {
        let doPrevent = true;
        const types = ['text', 'password', 'file', 'search', 'email', 'number', 'date', 'color', 'datetime', 'datetime-local', 'month', 'range', 'tel', 'time', 'url', 'week'];
        const d = $(event.srcElement || event.target);
        if (!d.prop('readonly') && !d.prop('disabled')) {
          if (d[0].isContentEditable) doPrevent = false;
          else if (d.is('input') && types.indexOf((d.attr('type') || '').toLowerCase()) > -1) doPrevent = false;
          else if (d.is('textarea')) doPrevent = false;
        }
        if (doPrevent) { event.preventDefault(); return false; }
      }
      return undefined;
    });
    $('#btnHome').click(() => { icsNavigateTo(cfg.urls.home); });
  },

  /** the ready-time boot: only on a page that emitted window.IcsShellConfig (the _Layout renders) */
  boot() {
    const g = globalThis, $ = jq();
    if (this.booted || !$ || !g.IcsShellConfig) return false;
    this.booted = true;
    const cfg = shellConfig();
    g.IcsShell = !cfg.framed;
    g.IcsFramed = !!cfg.framed;
    g.IcsMenuID = cfg.menuId;
    this.bootCommon($, cfg);
    this.bindDocument($);
    if (cfg.framed) this.bootFrameRender($); else this.bootShellRender($, cfg);
    return true;
  },
};
